import React from 'react';
import Form from '../Modules/form/components/Form';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import DocumentTitle from 'react-document-title';
import Breadcrumbs from './Breadcrumbs';
import TitleBar from '../Modules/layout/components/TitleBar';
import Grid from '@material-ui/core/Grid';
import TextInput from '../Modules/form/components/TextInput';
import SubmitButton from '../Modules/form/components/SubmitButton';
import { administrationSettingSchema } from '../Modules/administration/schema';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useCanWrite } from '../Modules/abilities/hooks';
import { ADMINISTRATION_SETTING_RESOURCE } from '../Modules/api/resources';
import LoadingBox from '../Modules/loading/components/LoadingBox';
import { useInitialValuesTrigger } from '../Modules/form/hooks';
import {
    storeAdministrationSetting,
    updateAdministrationSetting,
    useAdministrationSettings,
    selectAllAdministrationSettings,
} from '../Modules/administration/administrationSettingSlice';
import { useSelector } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Button from '../Modules/buttons/components/Button';
import { sendAgencyLocationSettingsRequest, sendMixedAgencyCareNeedsRequest } from '../Components/Export/hooks';
import { useUserEmployee } from '../Modules/auth/hooks';
import snakeCase from 'lodash/snakeCase';

// #1035 Feiertagsbetreuung am Kunden (BK-Verlauf): roter Hinweis ab / Frist im Hinweis /
// Ja-Nein-Eintrag wird entfernt am, je Weihnachten und Ostern
const HOLIDAY_CARE_SETTINGS = [
    { name: 'christmasCareReminderFrom', label: 'Weihnachten: Hinweis ab' },
    { name: 'christmasCareDeadline', label: 'Weihnachten: Frist' },
    { name: 'christmasCareResetOn', label: 'Weihnachten: Eintrag entfernen am' },
    { name: 'easterCareReminderFrom', label: 'Ostern: Hinweis ab' },
    { name: 'easterCareDeadline', label: 'Ostern: Frist' },
    { name: 'easterCareResetOn', label: 'Ostern: Eintrag entfernen am' },
];


/**
 * Requests a mail with an info export (nothing gets saved here). The receiver is
 * prefilled with defaultEmail and can be changed.
 */
const MailInfoBox = ({ title, defaultEmail, request, children }) => {
    const [email, setEmail] = useState(null);
    const [message, setMessage] = useState(null);
    useEffect(() => {
        // prefill once the user/employee is loaded, keep what the user typed
        setEmail((current) => (current === null && defaultEmail ? defaultEmail : current));
    }, [defaultEmail]);
    const handleSend = useCallback(async () => {
        setMessage(null);
        try {
            const { email: sentTo } = await request(email);
            setMessage({ error: false, text: `E-Mail wurde an ${sentTo} gesendet.` });
        } catch (err) {
            setMessage({
                error: true,
                text: err?.error || 'Die E-Mail konnte nicht gesendet werden.',
            });
        }
    }, [request, email]);

    return (
        <>
            <TitleBar>{title}</TitleBar>
            <Typography variant="body2" color="textSecondary" paragraph>
                {children}
            </Typography>
            <TextField
                label="Empfänger E-Mail"
                type="email"
                value={email || ''}
                onChange={(event) => setEmail(event.target.value)}
                fullWidth
                margin="dense"
            />
            <Box mt={1}>
                <Button variant="outlined" color="primary" onClick={handleSend} disabled={!email}>
                    Info
                </Button>
            </Box>
            {message && (
                <Typography variant="body2" color={message.error ? 'error' : 'textSecondary'}>
                    {message.text}
                </Typography>
            )}
        </>
    );
};

/**
 * Administration Page
 */
const AdministrationPage = () => {

    const SETTINGS_ID = 1;
    const DEFAULT_DAYS_BEFORE_NOTIFICATION_EMPLOYEE = 7;
    const DEFAULT_DAYS_BEFORE_NOTIFICATION_AGENCY = 9;
    const canWriteAdministrationSetting = useCanWrite(ADMINISTRATION_SETTING_RESOURCE);

    const dispatch = useDispatch();
    const { loading: administrationSettingLoading } = useAdministrationSettings();

    // get all administration setting values or default values if not set yet
    const administrationSettings = useSelector(selectAllAdministrationSettings);
    // sort by id descending to get the record with the highest id
    const activeAdministrationSetting = useMemo(() => {
        if (!administrationSettings || administrationSettings.length === 0) return null;
        return [...administrationSettings].sort((a, b) => b.id - a.id)[0];
    }, [administrationSettings]);

    const init = useMemo(() => {
         return {
             furtherAssignmentCheckDaysBaseEmployee: activeAdministrationSetting?.further_assignment_check_days_base_employee || DEFAULT_DAYS_BEFORE_NOTIFICATION_EMPLOYEE,
             furtherAssignmentCheckDaysBaseAgency: activeAdministrationSetting?.further_assignment_check_days_base_agency || DEFAULT_DAYS_BEFORE_NOTIFICATION_AGENCY,
             // contract fee of the PZH contracts, see apiv3 CalculateContractFee
             contractFeeFull: activeAdministrationSetting?.contract_fee_full ?? '',
             contractFeeHalfPercent: activeAdministrationSetting?.contract_fee_half_percent ?? '',
             contractLocationShareExpert: activeAdministrationSetting?.contract_location_share_expert ?? '',
             contractLocationShareLight: activeAdministrationSetting?.contract_location_share_light ?? '',
             // #1035 Feiertagsbetreuung am Kunden, 'TT.MM', see apiv3 AdministrationSetting::holidayCareSeasons
             ...HOLIDAY_CARE_SETTINGS.reduce((values, { name }) => ({
                 ...values,
                 [name]: activeAdministrationSetting?.[snakeCase(name)] ?? '',
             }), {}),
         };
    },
    [ activeAdministrationSetting ]);

    const loading = useMemo(() => {
            return (
                administrationSettingLoading === undefined ||
                administrationSettingLoading
            );
        }, [administrationSettingLoading]);

    const initialValues = useInitialValuesTrigger(activeAdministrationSetting, init, loading);

    // receiver of the mailed infos below defaults to the logged in user (employee email, else login email)
    const { user, employee } = useUserEmployee();
    const defaultInfoEmail = employee?.email || user?.email || '';

    const handleSubmit = useCallback(
        (values) => {
            const id = activeAdministrationSetting?.id || SETTINGS_ID;
            const prepped = { ...values, id };
            if (!activeAdministrationSetting) {
                return dispatch(storeAdministrationSetting(prepped));
            } else {
                return dispatch(updateAdministrationSetting(prepped));
            }
        },
        [dispatch, activeAdministrationSetting]
    );

    return (
        <>
            <DocumentTitle title="Administration" />
            <Container>
                <Breadcrumbs label="Administration" />
                <LoadingBox loading={loading} marginTop={2} marginBottom={2}>
                    <Form
                        validationSchema={administrationSettingSchema}
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        subject={activeAdministrationSetting || ADMINISTRATION_SETTING_RESOURCE}
                        enableReinitialize={true}
                    >
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TitleBar>Administration</TitleBar>
                            </Grid>
                            <Grid item xs={6}>
                                <TitleBar>Automatische Benachrichtigungen</TitleBar>
                                <Grid item xs={12}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextInput name="furtherAssignmentCheckDaysBaseEmployee" label="Tage vor Ende der letzten BK. Automatische Benachrichtigung an Mitarbeiter" disabled={!canWriteAdministrationSetting} fullWidth />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextInput name="furtherAssignmentCheckDaysBaseAgency" label="Tage vor Ende der letzten BK. Automatische Benachrichtigung an Agentur" disabled={!canWriteAdministrationSetting} fullWidth/>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <TitleBar>Vertragskosten (PzH-Vertrag)</TitleBar>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextInput
                                            name="contractFeeFull"
                                            label="Gebühr 100%-Vertrag"
                                            type="number"
                                            money
                                            disabled={!canWriteAdministrationSetting}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextInput
                                            name="contractFeeHalfPercent"
                                            label="50%-Vertrag im 1. Jahr: Anteil der Gebühr in %"
                                            type="number"
                                            disabled={!canWriteAdministrationSetting}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextInput
                                            name="contractLocationShareExpert"
                                            label="Standortanteil Expert"
                                            type="number"
                                            money
                                            disabled={!canWriteAdministrationSetting}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextInput
                                            name="contractLocationShareLight"
                                            label="Standortanteil Light"
                                            type="number"
                                            money
                                            disabled={!canWriteAdministrationSetting}
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={6}>
                                <TitleBar>Feiertagsbetreuung (Kunde, BK-Verlauf)</TitleBar>
                                <Typography variant="body2" color="textSecondary" paragraph>
                                    Jeweils als TT.MM. Ab „Hinweis ab“ erscheint bei aktiven Kunden ohne
                                    Eintrag ein roter Hinweis (mit der Frist), bis der Eintrag gemacht
                                    wurde. Am Tag „Eintrag entfernen am“ werden die Ja/Nein-Angaben
                                    automatisch geleert.
                                </Typography>
                                <Grid container spacing={2}>
                                    {HOLIDAY_CARE_SETTINGS.map(({ name, label }) => (
                                        <Grid item xs={4} key={name}>
                                            <TextInput
                                                name={name}
                                                label={label}
                                                placeholder="TT.MM."
                                                disabled={!canWriteAdministrationSetting}
                                                fullWidth
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Box textAlign="right" mt={1}>
                            <SubmitButton disabled={!canWriteAdministrationSetting} />
                        </Box>
                    </Form>
                </LoadingBox>

                <Box mt={4}>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <MailInfoBox
                                title="Info Agenturen & Standorte"
                                defaultEmail={defaultInfoEmail}
                                request={sendAgencyLocationSettingsRequest}
                            >
                                Sendet eine E-Mail mit einer Excel-Übersicht der Einstellungen von
                                Agenturen (Hauptagenturen und zugehörige Agenturen) und Standorten
                                (Zentrale, Zweigstellen, Leads an Zentrale umleiten, alle
                                Standortfelder) inkl. Auffälligkeiten.
                            </MailInfoBox>
                        </Grid>
                        <Grid item xs={6}>
                            <MailInfoBox
                                title="Info BK-Bedarfe mit mehreren Agenturen"
                                defaultEmail={defaultInfoEmail}
                                request={sendMixedAgencyCareNeedsRequest}
                            >
                                Sendet eine E-Mail mit allen BK-Bedarfen, deren BK-Einsätze zu
                                mehreren Agenturen gehören (CSV, eine Zeile pro BK-Bedarf und
                                Agentur; Regel: 1 BK-Bedarf = 1 Agentur), inkl. Kennzahlen.
                            </MailInfoBox>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </>
    );
};

export default AdministrationPage;